/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { BusinessPartner, BusinessPartnerUser, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IBusinessPartnersResources } from '../business-partners.props.autogenerated';
import { DataGrid, IDataGridColumn } from './data-grid';

/**
 * View model of business partner user, used to display.
 */
export interface IBusinessPartnerUserViewModel extends BusinessPartnerUser {
    name: string;
    spendingLimitStr: string;
}

/**
 * Props of BusinessPartnerUsersTable.
 */
export interface IBusinessPartnerUsersTableProps extends IComponentProps {
    className: string;
    pageSize?: number;
    businessPartner: BusinessPartner;
    resources: IBusinessPartnersResources;
    onSelect(businessPartnerUser: BusinessPartnerUser): void;
}

/**
 * BusinessPartnerUsersTable component.
 */
export class BusinessPartnerUsersTable extends React.Component<IBusinessPartnerUsersTableProps> {
    public shouldComponentUpdate(nextProps: Readonly<IBusinessPartnerUsersTableProps>): boolean {
        return this.props.id !== nextProps.id;
    }

    public render(): JSX.Element {
        return (
            <DataGrid
                {...this.props}
                loadData={this.getBusinessPartnerUsers}
                columns={this.getColumns()}
                defaultSorting={{ fieldName: 'Name', ascending: true }}
                searchPlaceholderLabel={this.props.resources.businessPartnerSearchCustomer}
            />
        );
    }

    /**
     * Get column definition of the table.
     * @returns - Columns.
     */
    private readonly getColumns = (): IDataGridColumn<IBusinessPartnerUserViewModel>[] => {
        return [
            {
                reactKey: 'name',
                name: this.props.resources.businessUserTableNameHeadingText,
                fieldName: 'name',
                isSearchable: true
            },
            {
                reactKey: 'email',
                name: this.props.resources.businessUserTableEmailHeadingText,
                fieldName: 'Email'
            },
            {
                reactKey: 'spendingLimit',
                name: this.props.resources.businessUserTableSpendingLimitHeadingText,
                className: `${this.props.className}__row__column-with-number`,
                fieldName: 'spendingLimitStr'
            },
            {
                reactKey: 'select-button',
                name: '',
                onRender: this.renderSelectButton,
                className: `${this.props.className}__row__column-select-button`
            }
        ];
    };

    /**
     * Renders the button to select a business partner user.
     * @param businessPartnerUser - The business partner user to select.
     * @returns - Button component.
     */
    private readonly renderSelectButton = (businessPartnerUser: IBusinessPartnerUserViewModel) => {
        return (
            <Button
                className={`${this.props.className}__row__select-button`}
                onClick={() => {
                    this.props.onSelect(businessPartnerUser);
                }}
                title={this.props.resources.select}
                aria-label={this.props.resources.select}
            />
        );
    };

    /**
     * Call retail proxy to get business partner users.
     * @returns - Business partner users view model.
     */
    private readonly getBusinessPartnerUsers = async (): Promise<IBusinessPartnerUserViewModel[]> => {
        const businessPartners = await BusinessPartnersDataActions.getUsersAsync(
            {
                callerContext: this.props.context.actionContext,
                bypassCache: 'get',
                queryResultSettings: {}
            },
            this.props.businessPartner.BusinessPartnerId
        );
        return businessPartners
            .filter(user => user.StatusValue === 1)
            .map(user => ({
                ...user,
                name: `${user.FirstName} ${user.LastName}`,
                spendingLimitStr: !ObjectExtensions.isNullOrUndefined(user.SpendingLimit)
                    ? this.props.context.cultureFormatter.formatCurrency(user.SpendingLimit.toString())
                    : ''
            }));
    };
}
